import { React, useState, useRef, useEffect, useLayoutEffect } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme, getColor } from '../../Styles/theme';
import { useLanguage } from '../../Context/LanguageContext';

import { ReactComponent as BlackPlusSvg } from '../../image/BlackPlus.svg';
import { ReactComponent as LeftArrowSvg } from '../../image/LeftArrow.svg';
import { ReactComponent as Check3Svg } from '../../image/Check3.svg';
import { ReactComponent as PlusSvg } from '../../image/Plus.svg';

export default function AvailableReserveLessonSelectBox(props) {
  const {
    storeId,
    storeLogo,
    membershipId,
    value,
    setValue,
    lessonData,
    lessonId,
    setLessonId
  } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

	const [isClicked, setIsClicked] = useState(false);
  const [options, setOptions] = useState(lessonData);
  const [visibleCount, setVisibleCount] = useState(value?.length || 0);

  const TopLevelRef = useRef();
	const SelectRef = useRef();
  const MeasureRef = useRef();
  const OptionRef = useRef();

  useEffect(() => {
    let newData = [];

    for (let i = 0; i < lessonData.length; i++) {
      if (lessonData[i].storeId === storeId) newData.push(lessonData[i]);
    }

    setOptions(newData);
  }, [storeId, lessonData])

	useEffect(() => {
		if (options.length === 0 || lessonId === -1 || lessonId === undefined || lessonId === '') {
      // setValue([]);
      return;
    }

    let lessonIdList = lessonId.split(',');
    let newValue = [];

    for (let i = 0; i < lessonIdList.length; i++) {
      if (options[options.findIndex(j => j.id === parseInt(lessonIdList[i]))] !== undefined) {
        newValue.push(options[options.findIndex(j => j.id === parseInt(lessonIdList[i]))]);
      }
    }

    setValue(newValue);
	}, [options, lessonId, membershipId])

  // 부모(OptionWrapper) 너비 안에 들어가는 chip 개수 계산 //
  useLayoutEffect(() => {
    function recalc() {
      const wrap = OptionRef.current;
      const measure = MeasureRef.current;

      if (!wrap || !measure) return;

      const W = wrap.clientWidth;
      const children = Array.from(measure.children);

      if (children.length === 0 || value.length === 0) {
        setVisibleCount(0);
        return;
      }

      const GAP = 5;
      const chipNodes = children.slice(0, value.length);
      const badgeNode = children[children.length - 1];
      const widths = chipNodes.map(n => n.getBoundingClientRect().width);
      const badgeW = badgeNode.getBoundingClientRect().width;

      const totalAll = widths.reduce((a, b) => a + b, 0) + Math.max(0, widths.length - 1) * GAP;

      if (totalAll <= W) {
        setVisibleCount(value.length);
        return;
      }

      let acc = 0;
      let n = 0;

      for (let i = 0; i < widths.length; i++) {
        const next = acc + widths[i] + (n > 0 ? GAP : 0);
        const withBadge = next + GAP + badgeW;

        if (withBadge <= W) {
          acc = next;
          n++;
        }
        else break;
      }

      setVisibleCount(n);
    }

    recalc();

    const wrap = OptionRef.current;

    if (!wrap) return;

    const ro = new ResizeObserver(recalc);
    ro.observe(wrap);

    return () => ro.disconnect();
  }, [value, isDark]);

  // 컴포넌트 밖을 클릭하면 셀렉트 창 닫기
  useEffect(() => {
    document.addEventListener("mousedown", onClickOutSide);

    return () => {
      document.removeEventListener("mousedown", onClickOutSide);
    }
  }, [])

  // 컴포넌트 밖을 클릭하면 셀렉트 창 닫기
  function onClickOutSide(e) {
    if (TopLevelRef.current !== e.target && !TopLevelRef.current.contains(e.target)) {
      SelectRef.current.style.border = '0px';
      SelectRef.current.style.height = `0px`;
      SelectRef.current.style.overflow = 'hidden';
      setIsClicked(false);
    }
  }

	function onClick() {
		if (isClicked) {
			SelectRef.current.style.border = '0px';
			SelectRef.current.style.height = `0px`;
			SelectRef.current.style.overflow = 'hidden';
			setIsClicked(false);
		}
		else {
			SelectRef.current.style.border = `1px solid ${theme.line}`;
			SelectRef.current.style.height = (32 * (options.length + 2)) > 224 ? `224px` : ((32 * (options.length + 2)) + 'px');
			SelectRef.current.style.overflow = 'auto';
			setIsClicked(true);
		}
	}

  // 코치 클릭시
	function optionalClick(index) {
    let newDefaultVal = value.slice();

    if (newDefaultVal.includes(options[index])) {
      let deletedIndex = 0;

      newDefaultVal.filter(function(item, idx) {
        if (options[index] === item) {
          deletedIndex = idx;
          return;
        }
      })

      newDefaultVal.splice(deletedIndex, 1);
      setValue(newDefaultVal);

      let lessonIds = '';

      for (let i = 0; i < newDefaultVal.length; i++) {
        if (lessonIds === '') lessonIds += newDefaultVal[i].id;
        else lessonIds += ',' + newDefaultVal[i].id;
      }

      setLessonId(lessonIds);
    }
    else {
      newDefaultVal.push(options[index]);
      setValue(newDefaultVal);

      if (lessonId === '') setLessonId(options[index].id.toString());
      else setLessonId(lessonId + ',' + options[index].id);
    }
	}

  // 모든 수업 추가 //
  function onClickAddAll() {
    setValue(options.slice());
    const lessonIds = options.map(o => o.id).join(',');
    setLessonId(lessonIds);
  }

	return (
    <TopLevelWrapper ref={TopLevelRef}>
      <DefaultView
        isClicked={isClicked}
        onClick={onClick}>
        <Logo src={storeLogo}/>
        <DefaultViewWrapper ref={OptionRef} isDefalt={true}>
          <ColorBoxWrapper>
            {value.slice(0, visibleCount).map((data, index) => (
              <ColorMapper key={index}>
                <LessonBox>
                  <ColorTag color={getColor(data.color, isDark)} />
                  <LessonName>{data.name}</LessonName>
                </LessonBox>
              </ColorMapper>
            ))}
          </ColorBoxWrapper>
          {
            visibleCount < value.length &&
            <LessonNameBox>
              <LessonName style={{ flexShrink: 0 }}>{`+${value.length - visibleCount}`}</LessonName>
            </LessonNameBox>
          }
          <MeasureLayer ref={MeasureRef} aria-hidden>
            {value.map((data, index) => (
              <ColorMapper key={'measure-' + index}>
                <LessonBox>
                  <ColorTag color={getColor(data.color, isDark)} />
                  <LessonName>{data.name}</LessonName>
                </LessonBox>
              </ColorMapper>
            ))}
            <LessonNameBox>
              <LessonName style={{ flexShrink: 0 }}>{`+${value.length}`}</LessonName>
            </LessonNameBox>
          </MeasureLayer>
        </DefaultViewWrapper>
        <StyledPlusIcon />
      </DefaultView>
      <SelectBox
        ref={SelectRef}
        isClicked={isClicked}>
        <SelectHeader onClick={onClick}>
          <SelectHeaderText>{t('classManage.selectOption')}</SelectHeaderText>
          <StyledLeftArrow isClicked={isClicked} />
        </SelectHeader>
        {
          options.map((data, index) =>
            <OptionWrapper key={index} onClick={() => optionalClick(index)} isDefalt={false}>
              <Option isClicked={value.includes(data)}>{data.name}</Option>
              <CheckBox isVisible={value.includes(data)}>
                <Check isVisible={value.includes(data)} />
              </CheckBox>
            </OptionWrapper>
          )
        }
        <AddAllButton onClick={onClickAddAll}>
          <StyledPlusIcon2 />
          <AddAllText>{t('classManage.addAllLesson')}</AddAllText>
        </AddAllButton>
      </SelectBox>
    </TopLevelWrapper>
  )
}

const TopLevelWrapper = styled.div`
	display: flex;
	flex-direction: column;

	// width: 344px;
  width: calc(100% - 10px);
	height: 32px;
`;

const DefaultView = styled.div`
  display: flex;
	flex-direction: row;

  justify-content: center;
  align-items: center;

  // padding: 0 8px 0 8px;
  padding: 0 8px 0 0;

	// width: 328px;
  // width: calc(100% - 18px);
  width: 100%;
	height: 30px;

  z-index: 3;

	border: 1px solid ${props => props.theme.line};
	border-radius: 8px;

  gap: 5px;

  overflow: hidden;

	background-color: ${props => props.theme.subBackground};
  // background-color: red;

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
  }
`;

const Logo = styled.img`
  width: 32px;
  height: 32px;

  object-fit: contain;
`;

const StyledPlusIcon = styled(BlackPlusSvg)`
  width: 20px;
  height: 20px;

  color: ${props => props.theme.subText};
  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
  }
`;

const SelectHeader = styled.div`
  flex-shrink: 0;

  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  padding: 0 15px 0 10px;

  box-sizing: border-box;
  width: 100%;
  height: 32px;

  &:hover {
    cursor: pointer;
  }
`;

const SelectHeaderText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.text};

  margin: 0;
  padding: 0;
`;

const StyledLeftArrow = styled(LeftArrowSvg)`
  width: 9px;
  height: auto;

  color: ${props => props.theme.line};

  transform: rotate(${props => props.isClicked ? 90 : -90}deg);
  transition: transform 0.3s ease-in-out;
`;

const CheckBox = styled.div`
  flex-shrink: 0;

  display: flex;
  justify-content: center;
  align-items: center;

  box-sizing: border-box;
  width: 20px;
  height: 20px;

  border: 1px solid ${props => props.isVisible ? props.theme.primary : props.theme.line};
  border-radius: 4px;
  background-color: ${props => props.isVisible ? props.theme.primary : 'none'};
`;

const Check = styled(Check3Svg)`
  width: 10px;
  height: auto;

  color: ${props => props.theme.background};

  visibility: ${props => props.isVisible ? 'visible' : 'hidden'};

  & path {
    fill: currentColor;
  }
`;

const SelectBox = styled.div`
	position: absolute;
	display: flex;
	flex-direction: column;

	justify-content: flex-start;
	align-items: flex-start;

  margin-top: 40px;

  // width: calc(45.45% - 18px);
  width: calc(100% - 40px);
	height: 0px;

  z-index: 5;

  border: 0px;
	border-radius: 5px;
	overflow: hidden;

	background-color: ${props => props.theme.subBackground};

  transition: all 0.3s ease-in-out;

	&:hover {
		cursor: pointer;
	}

	&::-webkit-scrollbar {
		display: none;
	}
`;

const DefaultViewWrapper = styled.div`
	display: flex;
	flex-direction: row;

  justify-content: ${props => props.isDefalt ? 'flex-start' : 'space-between'};
  align-items: center;

  padding: ${props => props.isDefalt ? '0' : '0 10px'};

  box-sizing: border-box;
  flex: ${props => props.isDefalt ? '1' : 'none'};
  width: ${props => props.isDefalt ? 'auto' : '100%'};
  min-width: 0;
  height: 32px;

  gap: ${props => props.isDefalt ? '5px' : '0'};

  transition: all 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const OptionWrapper = styled.div`
	display: flex;
	flex-direction: row;

  justify-content: space-between;
  align-items: center;

  padding: ${props => props.isDefalt ? '0' : '0 10px'};

  box-sizing: border-box;
  flex: ${props => props.isDefalt ? '1' : 'none'};
  width: ${props => props.isDefalt ? 'auto' : '100%'};
  min-width: 0;
  height: 32px;

  border-top: ${props => props.isDefalt ? 'none' : `1px solid ${props.theme.line}`};

  transition: all 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const Option = styled.p`
	font-family: NotoSansKR-Regular;
	font-size: 14px;

  color: ${props => props.isClicked ? props.theme.primary : props.theme.text};

  margin: 0px 0 0px 0;
  padding: 0 0 0 0;

  transition: color 0.3s ease-in-out;
`;

const AddAllButton = styled.div`
  flex-shrink: 0;

  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  box-sizing: border-box;
  width: 100%;
  height: 32px;

  gap: 6px;

  border-top: 1px solid ${props => props.theme.line};

  transition: background-color 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
      background-color: ${props => props.theme.primary};
      & p {
        color: ${props => props.theme.background};
      }
      & svg {
        color: ${props => props.theme.background};
      }
    }
  }
`;

const StyledPlusIcon2 = styled(PlusSvg)`
  width: 14px;
  height: 14px;

  color: ${props => props.theme.text};
  transition: color 0.3s ease-in-out;
`;

const AddAllText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.text};

  margin: 0;
  padding: 0;

  transition: color 0.3s ease-in-out;
`;

const ColorBoxWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  flex: 0 0 auto;
  min-width: 0;

  gap: 5px;
`;

const MeasureLayer = styled.div`
  position: absolute;
  visibility: hidden;
  pointer-events: none;

  display: flex;
  flex-direction: row;

  white-space: nowrap;

  top: -9999px;
  left: -9999px;

  gap: 5px;
`;

const ColorMapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;
`;

const LessonBox = styled.div`
  display: flex;
  flex-direction: row;

  justiry-content: flex-start;
  align-items: center;

  padding: 0 6px 0 0;

  box-sizing: border-box;
  max-width: 120px;
  height: 26px;

  gap: 5px;

  border-radius: 3px;
  background-color: ${props => props.theme.buttonFill};
`;

const LessonNameBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  padding: 0 10px;

  height: 22px;

  border-radius: 5px;
  background-color: ${props => props.theme.buttonFill};
`;

const ColorTag = styled.div`
  flex-shrink: 0;

  width: 6px;
  height: 100%;

  border-radius: 2px 0 0 2px;
  background-color: ${props => props.color};
`;

const LessonName = styled.span`
  max-width: 105px;

  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.text};

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
